import React, { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { Col, Row, Spin } from 'antd';
import pluralize from 'pluralize';
import { useDidUpdate } from 'rooks';

import ContractorCompanyFilter, {
  ContractorCompanyFilters,
} from './ContractorCompanyFilter';
import { useDebounce } from 'hooks/useDebounce';
import { ContractorCompanyFilterTypes } from '../enums';
import ContractorCompanySearch from './ContractorCompanySearch';
import { ContractorCompanyItemViewModel } from 'api/adminApiClient';
import { getContractorCompaniesParentSCAgents } from 'redux/reducers/contractorCompanySlice';
import ContractorCompanyCard from 'features/worker/WorkersPage/ContractorCompaniesSection/ContractorCompanyCard';
import { typeConversionToStringArray } from 'utils/convertUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';

export default function AdminContractorCompaniesSection() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { query, company, section } = queryString.parse(location.search);
  const initialFilters: ContractorCompanyFilters = {
    [ContractorCompanyFilterTypes.Company]:
      typeConversionToStringArray(company),
  };
  const [filters, setFilters] =
    useState<ContractorCompanyFilters>(initialFilters);
  const [contractorCompanies, setContractorCompanies] = useState<
    ContractorCompanyItemViewModel[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const companiesTotalCountLabel = pluralize(
    'contractor company',
    totalCount,
    false,
  );
  const parentSCAgentsCountLabel = pluralize(
    'parent SC agent',
    totalCount,
    false,
  );
  const totalCountLabel = `${totalCount} ${companiesTotalCountLabel} and ${parentSCAgentsCountLabel}`;
  useDidUpdate(() => {
    const queryData = {
      query,
      section,
      [ContractorCompanyFilterTypes.Company]:
        filters[ContractorCompanyFilterTypes.Company],
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters, query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setContractorCompanies([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const debouncedLoadContractorCompanies = useDebounce(initInfinityScroll, 100);

  useEffect(() => {
    debouncedLoadContractorCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, query]);

  const onSetContractorCompanies = useCallback(
    (result) => {
      const loadedContractorCompanies = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newContractorCompanies = [
        ...contractorCompanies,
        ...loadedContractorCompanies,
      ];
      const hasMoreRows =
        !!newContractorCompanies.length &&
        newContractorCompanies.length < totalCount;

      setHasMore(hasMoreRows);
      setContractorCompanies(newContractorCompanies);
      setTotalCount(totalCount);
    },
    [contractorCompanies],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getContractorCompaniesParentSCAgents({
          requestId,
          pageNumber,
          searchQuery: query ? query.trim() : undefined,
          companyIds: filters[ContractorCompanyFilterTypes.Company],
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getContractorCompaniesParentSCAgents.fulfilled.match(result)) {
          onSetContractorCompanies(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetContractorCompanies, filters, query],
  );

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={12} lg={6}>
              <ContractorCompanySearch searchQuery={query} />
            </Col>

            <Col xs={24} md={12} lg={6}>
              <ContractorCompanyFilter
                filters={filters}
                onChange={setFilters}
                value={filters[ContractorCompanyFilterTypes.Company]}
              />
            </Col>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{totalCountLabel}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">
                      You have no contractor companies or parent SC agents.
                    </span>
                  )}
                </>
              )}

              {isVisible && (
                <InfiniteScroll
                  pageStart={-1}
                  loadMore={onLoadMore}
                  hasMore={hasMore}
                  loader={<Spin key={0} />}
                  useWindow={false}
                  getScrollParent={() => infiniteScrollParentRef.current}
                >
                  <Row gutter={[20, 20]}>
                    {contractorCompanies.map((company, index) => (
                      <Col
                        md={24}
                        lg={12}
                        className="d-flex"
                        key={`${company.id}-${index}`}
                      >
                        <ContractorCompanyCard company={company} />
                      </Col>
                    ))}
                  </Row>
                </InfiniteScroll>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
